import { useEffect, useState } from 'react';
import type { Invite, Role } from '@callzone/shared';
import { api } from '../lib/api';
import { useActiveGuild, useStore } from '../store';
import { CloseIcon } from './icons';

const EXPIRY_OPTIONS = [
  { label: 'Nunca', hours: 0 },
  { label: '1 hora', hours: 1 },
  { label: '1 dia', hours: 24 },
  { label: '7 dias', hours: 168 },
];

const USE_OPTIONS = [
  { label: '1 pessoa', value: 1 },
  { label: '5 pessoas', value: 5 },
  { label: '12 pessoas', value: 12 },
  { label: 'Sem limite', value: 0 },
];

/**
 * Cargo que o convite concede por padrao. Quem entra por convite e membro da
 * guild, entao ja nasce com o cargo Membro em vez de so @everyone — que e
 * invisivel e nao separa quem entrou de quem nunca entrou.
 *
 * Se a guild nao tiver esse cargo (foi apagado, ou e anterior ao padrao), o
 * seletor volta para "Nenhum" em vez de escolher um cargo qualquer.
 */
const CARGO_PADRAO = 'Membro';

function cargoPadraoId(roles: Role[]): string {
  const role = roles.find((r) => !r.isDefault && r.name.toLowerCase() === CARGO_PADRAO.toLowerCase());
  return role?.id ?? '';
}

export function InviteModal({ onClose }: { onClose: () => void }) {
  const guild = useActiveGuild();
  const toast = useStore((s) => s.toast);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [loading, setLoading] = useState(true);
  const [maxUses, setMaxUses] = useState(1);
  const [expiresInHours, setExpiresInHours] = useState(0);
  const [grantRoleId, setGrantRoleId] = useState(() => cargoPadraoId(guild?.roles ?? []));
  const [creating, setCreating] = useState(false);
  // Marca se a pessoa mexeu no seletor: a partir dai o padrao para de mandar.
  const [cargoEscolhido, setCargoEscolhido] = useState(false);

  /*
   * Os cargos podem chegar depois da primeira renderizacao (guild ainda
   * carregando, ou alguem acabou de criar o cargo Membro). Sem isto o seletor
   * ficaria preso em "Nenhum" mesmo com o cargo existindo.
   */
  useEffect(() => {
    if (cargoEscolhido || grantRoleId) return;
    const padrao = cargoPadraoId(guild?.roles ?? []);
    if (padrao) setGrantRoleId(padrao);
  }, [guild?.roles, cargoEscolhido, grantRoleId]);

  useEffect(() => {
    if (!guild) return;
    void api
      .invites(guild.guild.id)
      .then((result) => setInvites(result.invites))
      .catch((error: Error) => toast(error.message, 'error'))
      .finally(() => setLoading(false));
  }, [guild?.guild.id, toast, guild]);

  if (!guild) return null;

  const nomeDoCargo = (roleId: string | null) =>
    roleId ? (guild.roles.find((r) => r.id === roleId)?.name ?? null) : null;

  async function create() {
    setCreating(true);
    try {
      const { invite } = await api.createInvite(guild!.guild.id, {
        maxUses,
        expiresInHours: expiresInHours || undefined,
        grantRoleId: grantRoleId || null,
      });
      setInvites((current) => [invite, ...current]);
      await navigator.clipboard.writeText(invite.code).catch(() => undefined);
      toast('Convite criado e copiado para a area de transferencia.');
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Falha ao criar convite.', 'error');
    } finally {
      setCreating(false);
    }
  }

  async function revoke(code: string) {
    try {
      await api.revokeInvite(code);
      setInvites((current) => current.filter((i) => i.code !== code));
      toast('Convite revogado.');
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Falha ao revogar.', 'error');
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Convites de {guild.guild.name}</h2>
          <button className="icon-btn" onClick={onClose} title="Fechar">
            <CloseIcon />
          </button>
        </div>

        <div className="modal-body">
          <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 16 }}>
            Como nao existe cadastro aberto, o convite e a unica porta de entrada. Quem receber o
            codigo escolhe um apelido e ja entra.
          </p>

          <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
            <div className="field" style={{ flex: 1, marginBottom: 0 }}>
              <label>Usos</label>
              <select value={maxUses} onChange={(e) => setMaxUses(Number(e.target.value))}>
                {USE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="field" style={{ flex: 1, marginBottom: 0 }}>
              <label>Expira em</label>
              <select
                value={expiresInHours}
                onChange={(e) => setExpiresInHours(Number(e.target.value))}
              >
                {EXPIRY_OPTIONS.map((option) => (
                  <option key={option.hours} value={option.hours}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="field">
            <label>Cargo ao entrar</label>
            <select
              value={grantRoleId}
              onChange={(e) => {
                setCargoEscolhido(true);
                setGrantRoleId(e.target.value);
              }}
            >
              <option value="">Nenhum (so @everyone)</option>
              {guild.roles
                .filter((r) => !r.isDefault)
                .map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
            </select>
            <div className="hint">
              {grantRoleId
                ? `Quem usar este convite ja entra com o cargo ${nomeDoCargo(grantRoleId) ?? ''}.`
                : 'Sem cargo, a pessoa entra so com @everyone.'}
            </div>
          </div>

          <button className="btn full" onClick={() => void create()} disabled={creating}>
            {creating ? 'Gerando...' : 'Gerar convite'}
          </button>

          <div className="member-group-title" style={{ marginTop: 20 }}>
            Convites ativos
          </div>
          {loading && <div style={{ color: 'var(--text-dim)' }}>Carregando...</div>}
          {!loading && invites.length === 0 && (
            <div style={{ color: 'var(--text-dim)', fontSize: 13 }}>Nenhum convite ativo.</div>
          )}
          {invites.map((invite) => (
            <div key={invite.code} className="list-row">
              <div className="grow">
                <div className="invite-code">{invite.code}</div>
                <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>
                  {invite.maxUses === 0
                    ? `${invite.uses} usos · sem limite`
                    : `${invite.uses}/${invite.maxUses} usos`}
                  {invite.expiresAt
                    ? ` · expira ${new Date(invite.expiresAt).toLocaleString('pt-BR')}`
                    : ' · nao expira'}
                  {nomeDoCargo(invite.grantRoleId)
                    ? ` · entra como ${nomeDoCargo(invite.grantRoleId)}`
                    : ' · sem cargo'}
                </div>
              </div>
              <button
                className="btn ghost small"
                onClick={() => void navigator.clipboard.writeText(invite.code).then(() => toast('Codigo copiado.'))}
              >
                Copiar
              </button>
              <button className="btn danger small" onClick={() => void revoke(invite.code)}>
                Revogar
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
